/**
 * NOTE TASK PROTOTYPE PANEL
 *
 * Inline task panel rendered from note success flows when a saved note is marked for tasks.
 *
 * Key Features:
 * - Stays inside the note confirmation experience instead of navigating away from note workflows
 * - Seeds task fields from the saved note title and content
 * - Shows related account or contact context for quick follow-up review
 * - Supports multiple assignees via searchable checkbox selection
 * - Submits into the persisted task API while keeping note context intact
 *
 * Changes:
 * - Converted the note follow-up task panel from a local prototype into a persisted task entry flow
 * - Prevents past due dates so note-linked task entry matches the task workflow rules
 * - Requires a valid due date before submitting a note-linked task
 * - Preserves title-first Description payloads through separate joined Title and Notes fields
 * - Matches shared task-dialog presentation while preserving note-linked behavior
 * - Matches the main task composer assignee trigger, search, and checkbox multi-select design
 * - Matches the main task composer full-field due-date picker and trailing calendar design
 * - Opens Assignee options above the trigger when the dialog lacks room below
 * - Uses the shared responsive two-section task layout with CRM context above the Task field
 * - Includes a default-on assignment email choice that resets whenever the dialog opens
 * - Aligns the assignment email choice left of the footer actions in one compact row
 * - Defaults existing-note task assignment to the authenticated task creator
 * - Adds the shared Medium-defaulted segmented Priority selector and due-date shortcuts
 * - Limits level-restricted note tasks to assignees at or above the note's minimum access level
 * - Limits Prospect and Commercial note tasks to Business Development/Commercial Admin members plus the note creator
 * - Keeps level-note creators assignable even below their selected note level
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Note } from '../../models/Note';
import { isAccessLevel, type AccessLevel } from '../../constants/noteVisibilityConstants';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import {
  TASK_DESCRIPTION_TITLE_REQUIRED_ERROR,
  getTaskDueDateValidationError,
  getTaskTitleFromDescription,
  getTodayDateValue,
  type TaskPriority,
} from '../../models/Task';
import { request } from '../../services/dataService';
import {
  getAccountAudienceTeamIds,
  isAccountAudienceMember,
  isAccountAudienceTeam,
} from '../../utils/accountAssigneeAudience';
import { Button, Input } from '../primitives';
import {
  TaskDueDateField,
  TaskPopupFooter,
  TaskPopupLayout,
  TaskPriorityControl,
  TaskTextFields,
  composeTaskDescription,
  splitTaskDescription,
} from '../task/popup';

export interface NoteTaskContextEntity {
  id: number;
  personId: number;
  name: string;
  type: string;
  email?: string;
  teamId?: number;
}

export interface NoteTaskFormValues {
  title: string;
  description: string;
  priority: TaskPriority;
  assigneeIds: number[];
  assigneeNames: string[];
  dueDate: string;
  sendEmail: boolean;
}

interface NoteTaskPrototypePanelProps {
  savedNote: Note;
  selectedEntity: NoteTaskContextEntity | null;
  currentUserId?: number | null;
  open?: boolean;
  onClose: () => void;
  onSkipTask?: () => void;
  onSubmitTask: (values: NoteTaskFormValues) => Promise<void> | void;
  submitLabel?: string;
}

interface AssigneeOption {
  id: number;
  name: string;
  email?: string;
  accessLevel?: AccessLevel;
}

interface UserTeamMembership {
  userId: number | null;
  teamId: number | null;
}

interface AssigneeTeam {
  teamId: number;
  teamName: string;
}

type AssigneeDropdownPlacement = 'top' | 'bottom';

// Popup chrome, the search field, and three typical assignee rows.
const ASSIGNEE_DROPDOWN_MINIMUM_SPACE = 220;
const ASSIGNEE_DROPDOWN_GAP = 8;
const getNoteTeamIds = (teamIds: string | undefined) => (
  (teamIds ?? '')
    .split(',')
    .map((teamId) => Number(teamId.trim()))
    .filter((teamId) => Number.isInteger(teamId) && teamId > 0)
);

const getAssigneeDropdownBoundary = (element: HTMLElement) => {
  let ancestor = element.parentElement;

  while (ancestor) {
    const styles = window.getComputedStyle(ancestor);
    const overflow = `${styles.overflow} ${styles.overflowY}`;
    if (/(auto|scroll|hidden|clip)/.test(overflow)) {
      const rect = ancestor.getBoundingClientRect();
      return {
        top: Math.max(0, rect.top),
        bottom: Math.min(window.innerHeight, rect.bottom),
      };
    }
    ancestor = ancestor.parentElement;
  }

  return { top: 0, bottom: window.innerHeight };
};

const NoteTaskPrototypePanel: React.FC<NoteTaskPrototypePanelProps> = ({
  savedNote,
  selectedEntity,
  currentUserId,
  open = true,
  onClose,
  onSkipTask,
  onSubmitTask,
  submitLabel = 'Save Task',
}) => {
  const initialDescriptionFields = splitTaskDescription(savedNote.content || '');
  const [taskTitle, setTaskTitle] = useState(initialDescriptionFields.title);
  const [taskNotes, setTaskNotes] = useState(initialDescriptionFields.notes);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [availableAssignees, setAvailableAssignees] = useState<AssigneeOption[]>([]);
  const [assigneeMemberships, setAssigneeMemberships] = useState<UserTeamMembership[]>([]);
  const [assigneeTeams, setAssigneeTeams] = useState<AssigneeTeam[]>([]);
  const [hasAccountAudienceMembershipError, setHasAccountAudienceMembershipError] = useState(false);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<number[]>([]);
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false);
  const [assigneeError, setAssigneeError] = useState<string | null>(null);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [assigneeDropdownPlacement, setAssigneeDropdownPlacement] = useState<AssigneeDropdownPlacement>('bottom');
  const [dueDate, setDueDate] = useState('');
  const [dueDateError, setDueDateError] = useState<string | null>(null);
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [sendEmail, setSendEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const assigneePickerRef = useRef<HTMLDivElement>(null);
  const minDueDate = useMemo(() => getTodayDateValue(), []);
  const needsTeamMemberships = savedNote.visibilityMode === 'teams';
  const minimumNoteAccessLevel = savedNote.visibilityMode === 'levels' && isAccessLevel(savedNote.minimumLevel)
    ? savedNote.minimumLevel
    : null;
  const noteTeamIds = useMemo(() => getNoteTeamIds(savedNote.teamIds), [savedNote.teamIds]);
  const accountAudienceTeamIds = useMemo(
    () => getAccountAudienceTeamIds(noteTeamIds, assigneeTeams),
    [assigneeTeams, noteTeamIds]
  );
  const hasAccountAudienceRestriction = accountAudienceTeamIds.size > 0;

  const eligibleAssignees = useMemo(() => (
    availableAssignees.filter((assignee) => {
      if (hasAccountAudienceMembershipError) {
        return false;
      }

      const isNoteCreator = assignee.id === savedNote.createdBy;
      const meetsLevelRequirement = minimumNoteAccessLevel === null
        || isNoteCreator
        || (isAccessLevel(assignee.accessLevel) && assignee.accessLevel >= minimumNoteAccessLevel);
      const meetsAccountAudienceRequirement = !hasAccountAudienceRestriction
        || isNoteCreator
        || isAccountAudienceMember(assignee.id, accountAudienceTeamIds, assigneeMemberships);

      return meetsLevelRequirement && meetsAccountAudienceRequirement;
    })
  ), [accountAudienceTeamIds, assigneeMemberships, availableAssignees, hasAccountAudienceMembershipError, hasAccountAudienceRestriction, minimumNoteAccessLevel, savedNote.createdBy]);

  const selectedAssignees = useMemo(() => (
    selectedAssigneeIds.flatMap((id) => {
      const assignee = eligibleAssignees.find((candidate) => candidate.id === id);
      return assignee ? [assignee] : [];
    })
  ), [eligibleAssignees, selectedAssigneeIds]);

  const updateAssigneeDropdownPlacement = useCallback(() => {
    const picker = assigneePickerRef.current;
    if (!picker) {
      return;
    }

    const triggerRect = picker.getBoundingClientRect();
    const boundary = getAssigneeDropdownBoundary(picker);
    const spaceBelow = Math.max(0, boundary.bottom - triggerRect.bottom - ASSIGNEE_DROPDOWN_GAP);
    const spaceAbove = Math.max(0, triggerRect.top - boundary.top - ASSIGNEE_DROPDOWN_GAP);
    const nextPlacement = spaceBelow < ASSIGNEE_DROPDOWN_MINIMUM_SPACE && spaceAbove > spaceBelow
      ? 'top'
      : 'bottom';

    setAssigneeDropdownPlacement((currentPlacement) => (
      currentPlacement === nextPlacement ? currentPlacement : nextPlacement
    ));
  }, []);

  const filteredAssignees = useMemo(() => {
    const trimmedQuery = assigneeQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return eligibleAssignees;
    }

    return eligibleAssignees
      .filter(({ name, email }) => (
        name.toLowerCase().includes(trimmedQuery)
        || email?.toLowerCase().includes(trimmedQuery)
      ));
  }, [assigneeQuery, eligibleAssignees]);

  const selectedAssigneeLabel = selectedAssignees.length
    ? selectedAssignees.map(({ name }) => name).join(', ')
    : 'Select assignees';

  useEffect(() => {
    let isMounted = true;

    const loadAssignees = async () => {
      setIsLoadingAssignees(true);
      setAssigneeError(null);
      setHasAccountAudienceMembershipError(false);

      try {
        const [users, teams] = await Promise.all([
          request(API_ENDPOINTS.GET_USERS),
          needsTeamMemberships ? request(API_ENDPOINTS.GET_TEAMS) : Promise.resolve([]),
        ]);
        if (!isMounted) {
          return;
        }

        setAvailableAssignees(
          (Array.isArray(users) ? users : []).map((user) => ({
            id: user.id,
            name: user.name,
            email: user.email,
            accessLevel: isAccessLevel(user.accessLevel) ? user.accessLevel : undefined,
          }))
        );
        const resolvedTeams = (Array.isArray(teams) ? teams : [])
          .filter((team) => typeof team.teamId === 'number' && typeof team.teamName === 'string')
          .map((team) => ({ teamId: team.teamId, teamName: team.teamName }));
        if (needsTeamMemberships && !Array.isArray(teams)) {
          throw new Error('Invalid team directory response');
        }
        setAssigneeTeams(resolvedTeams);

        if (!needsTeamMemberships) {
          setAssigneeMemberships([]);
          return;
        }

        try {
          const memberships = await request(API_ENDPOINTS.GET_TEAMS_USERS);
          if (!isMounted) {
            return;
          }
          if (!Array.isArray(memberships)) {
            throw new Error('Invalid team membership response');
          }

          setAssigneeMemberships(memberships.map((membership) => ({
            userId: typeof membership.userId === 'number' ? membership.userId : null,
            teamId: typeof membership.teamId === 'number' ? membership.teamId : null,
          })));
        } catch (membershipError) {
          if (!isMounted) {
            return;
          }

          const hasAccountAudienceTeam = noteTeamIds.some((teamId) => resolvedTeams.some((team) => (
            team.teamId === teamId && isAccountAudienceTeam(team.teamName)
          )));
          setAssigneeMemberships([]);

          if (hasAccountAudienceTeam) {
            console.error('Failed to load account-audience assignee memberships:', membershipError);
            setHasAccountAudienceMembershipError(true);
            setAssigneeError('Unable to load account audience assignees right now.');
          }
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to load assignee options:', error);
        if (needsTeamMemberships) {
          setHasAccountAudienceMembershipError(true);
        }
        setAssigneeError('Unable to load assignees right now.');
      } finally {
        if (isMounted) {
          setIsLoadingAssignees(false);
        }
      }
    };

    loadAssignees();

    return () => {
      isMounted = false;
    };
  }, [needsTeamMemberships, noteTeamIds]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextDescriptionFields = splitTaskDescription(savedNote.content || '');
    setTaskTitle(nextDescriptionFields.title);
    setTaskNotes(nextDescriptionFields.notes);
    setDescriptionError(null);
    setSelectedAssigneeIds(
      typeof currentUserId === 'number' && Number.isFinite(currentUserId) && currentUserId > 0
        ? [currentUserId]
        : []
    );
    setAssigneeQuery('');
    setIsAssigneeDropdownOpen(false);
    setDueDate('');
    setDueDateError(null);
    setPriority('Medium');
    setSendEmail(true);
    setIsSubmitting(false);
  }, [currentUserId, open, savedNote]);

  useEffect(() => {
    if (!open || (!hasAccountAudienceRestriction && minimumNoteAccessLevel === null) || !availableAssignees.length) {
      return;
    }

    const eligibleAssigneeIds = new Set(eligibleAssignees.map(({ id }) => id));
    setSelectedAssigneeIds((previousAssigneeIds) => {
      const nextAssigneeIds = previousAssigneeIds.filter((id) => eligibleAssigneeIds.has(id));
      return nextAssigneeIds.length === previousAssigneeIds.length ? previousAssigneeIds : nextAssigneeIds;
    });
  }, [availableAssignees, eligibleAssignees, hasAccountAudienceRestriction, minimumNoteAccessLevel, open]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneePickerRef.current?.contains(event.target as Node)) {
        setIsAssigneeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isAssigneeDropdownOpen) {
      setAssigneeQuery('');
    }
  }, [isAssigneeDropdownOpen]);

  useEffect(() => {
    if (!isAssigneeDropdownOpen) {
      return;
    }

    updateAssigneeDropdownPlacement();
    window.addEventListener('resize', updateAssigneeDropdownPlacement);
    window.addEventListener('scroll', updateAssigneeDropdownPlacement, true);
    return () => {
      window.removeEventListener('resize', updateAssigneeDropdownPlacement);
      window.removeEventListener('scroll', updateAssigneeDropdownPlacement, true);
    };
  }, [isAssigneeDropdownOpen, updateAssigneeDropdownPlacement]);

  const handleAssigneeToggle = (assignee: AssigneeOption) => {
    setSelectedAssigneeIds((previousAssigneeIds) => (
      previousAssigneeIds.includes(assignee.id)
        ? previousAssigneeIds.filter((assigneeId) => assigneeId !== assignee.id)
        : [...previousAssigneeIds, assignee.id]
    ));
  };

  const contextLabel = selectedEntity
    ? `${selectedEntity.type}: ${selectedEntity.name}`
    : savedNote.accountName
      ? `Account: ${savedNote.accountName}`
      : savedNote.contactName
        ? `Contact: ${savedNote.contactName}`
        : 'No related CRM context';

  return (
    <div data-testid="note-task-modal-form" className="p-5 text-left sm:p-6">
      <h3 className="sr-only">Create follow-up task</h3>
      <TaskPopupLayout
        primary={(
          <>
            <div data-testid="note-task-context">
              <div className={`mb-2 text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`}>Account or Contact</div>
              <div className={`rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm`}>
                <div className={THEME_CLASSES.text.primary}>{contextLabel}</div>
                <div className={`mt-1 text-xs ${THEME_CLASSES.text.secondary}`}>Source note ID: {savedNote.id || 'Pending response'}</div>
              </div>
            </div>
            <TaskTextFields
              idPrefix="note-task-prototype"
              title={taskTitle}
              notes={taskNotes}
              onTitleChange={(value) => {
                setTaskTitle(value);
                if (descriptionError && getTaskTitleFromDescription(value)) setDescriptionError(null);
              }}
              onNotesChange={setTaskNotes}
              invalid={Boolean(descriptionError)}
              notesRows={10}
            />
          </>
        )}
        details={(
          <div data-testid="note-task-details-grid" className="space-y-5">
            <TaskPriorityControl idPrefix="note-task-prototype" value={priority} onChange={setPriority} />
            <div>
              <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`} htmlFor="note-task-prototype-assignees">Assignee</label>
              <div ref={assigneePickerRef} className="relative">
                <button
                  id="note-task-prototype-assignees"
                  type="button"
                  onClick={() => {
                    if (!isAssigneeDropdownOpen) updateAssigneeDropdownPlacement();
                    setIsAssigneeDropdownOpen((isOpen) => !isOpen);
                  }}
                  className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} px-3 py-2 text-left text-sm ${THEME_CLASSES.input.text} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  aria-haspopup="true"
                  aria-expanded={isAssigneeDropdownOpen}
                >
                  <span className="min-w-0 truncate">{selectedAssigneeLabel}</span>
                  <ChevronDownIcon className={`h-4 w-4 shrink-0 ${THEME_CLASSES.text.secondary}`} aria-hidden="true" />
                </button>
                {isAssigneeDropdownOpen && (
                  <div className={`absolute left-0 right-0 z-20 rounded-xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} p-2 shadow-lg ${assigneeDropdownPlacement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}`} role="group" aria-label="Assignee options" data-assignee-dropdown-placement={assigneeDropdownPlacement}>
                    <div className="relative mb-2">
                      <MagnifyingGlassIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`} aria-hidden="true" />
                      <Input type="search" value={assigneeQuery} onChange={(event) => setAssigneeQuery(event.target.value)} placeholder="Search assignees..." aria-label="Search assignees" className="pl-9" autoFocus />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {filteredAssignees.map((assignee) => (
                        <label key={assignee.id} className={`flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2 text-sm ${THEME_CLASSES.text.primary} hover:bg-slate-100 dark:hover:bg-slate-900`}>
                          <input type="checkbox" checked={selectedAssigneeIds.includes(assignee.id)} onChange={() => handleAssigneeToggle(assignee)} className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          <span className="min-w-0">
                            <span className="block truncate">{assignee.name}</span>
                            {assignee.email && <span className={`block truncate text-xs ${THEME_CLASSES.text.secondary}`}>{assignee.email}</span>}
                          </span>
                        </label>
                      ))}
                      {!isLoadingAssignees && !filteredAssignees.length && !assigneeError && <p className={`px-3 py-4 text-center text-sm ${THEME_CLASSES.text.secondary}`}>No assignees found.</p>}
                      {isLoadingAssignees && <p className={`px-3 py-4 text-center text-sm ${THEME_CLASSES.text.secondary}`}>Loading assignees...</p>}
                    </div>
                  </div>
                )}
              </div>
              {assigneeError && <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{assigneeError}</div>}
            </div>
            <TaskDueDateField
              id="note-task-prototype-due-date"
              min={minDueDate}
              value={dueDate}
              onChange={(value) => {
                setDueDate(value);
                setDueDateError(getTaskDueDateValidationError(value, minDueDate));
              }}
              invalid={Boolean(dueDateError)}
              iconTestId="note-task-due-date-icon"
            />
          </div>
        )}
      />

      {descriptionError && <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{descriptionError}</div>}
      {dueDateError && <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{dueDateError}</div>}

      <TaskPopupFooter
        testId="note-task-actions"
        checked={sendEmail}
        onCheckedChange={setSendEmail}
        actions={(
          <>
            <Button onClick={onSkipTask ?? onClose} variant="secondary" size="sm">Not now</Button>
            <Button
              onClick={async () => {
                const normalizedTitle = getTaskTitleFromDescription(taskTitle);
                if (!normalizedTitle) {
                  setDescriptionError(TASK_DESCRIPTION_TITLE_REQUIRED_ERROR);
                  return;
                }

                const validationError = getTaskDueDateValidationError(dueDate, minDueDate);
                if (validationError) {
                  setDueDateError(validationError);
                  return;
                }

                setDueDateError(null);
                setIsSubmitting(true);
                try {
                  await onSubmitTask({
                    title: normalizedTitle,
                    description: composeTaskDescription(taskTitle, taskNotes),
                    priority,
                    assigneeIds: selectedAssignees.map(({ id }) => id),
                    assigneeNames: selectedAssignees.map(({ name }) => name),
                    dueDate,
                    sendEmail,
                  });
                } finally {
                  setIsSubmitting(false);
                }
              }}
              size="sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : submitLabel}
            </Button>
          </>
        )}
      />
    </div>
  );
};

export default NoteTaskPrototypePanel;
